import { motion } from "framer-motion";
import pluralize from "pluralize";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { type PressEvent, useHover, usePress } from "react-aria";
import { useErrorBoundary } from "react-error-boundary";
import {
  ActionButton,
  Card,
  CardRowSimple,
  CardSection,
  DialogComponent,
  HStack,
  MenuOptionItem,
  notify,
  Tag,
  TriggerButton,
  TriggerButtonGroup,
  VStack,
} from "widgets";
import { useLongLoading } from "widgets/hooks";
import { colors, sortAlpha } from "widgets/utils";
import { useShallow } from "zustand/shallow";

import { fetchFeatureInfo } from "../../../api/fetch.ts";
import { sendSyntenyRenderMessage } from "../../../main-canvas/messages/post-alignment-messages.ts";
import { refreshTracks } from "../../../main-canvas/messages/post-track-messages.ts";
import { refreshMain } from "../../../main-canvas/render-main.ts";
import { SyntenyTypeEnum, TrackEnum, TrackTextEnum } from "../../../openapi";
import { syntenyViewerDefaultTracks } from "../../../settings.ts";
import { useGenomeViewerStore } from "../../../store/store.ts";
import { type FeatureInfo, type MainThreadHoverMessage } from "../../../types.ts";
import { align, type MemberItem } from "../../../utils/align.ts";
import { FetchError } from "../../../utils/errors.ts";
import { formatRegion } from "../../../utils/region.ts";
import type { TrackConfig } from "../../../utils/tracks/create-tracks.ts";
import { getGenomicFeatureTypeFromTrackType, getTrackTypeBySyntenyType } from "../../../utils/type-conversion.ts";
import { handleFeaturePopover } from "../utils/handle-feature-popover.ts";
import { type GenomeMember, getFeaturesFamilyMembersByGenomeOrThrow } from "../utils/process-genome-data.ts";
import { OpenGenomeViewerButton } from "./OpenGenomeViewerButton.tsx";
import { OpenSummaryPageButton } from "./OpenSummaryPageButton.tsx";
import { Skeleton } from "./Skelton.tsx";
import { TrackFeatureTooltip } from "./TrackFeatureTooltip.tsx";
import { TrackNuFeatureTooltip } from "./TrackNuFeatureTooltip.tsx";

export function InfoPopover() {
  const { showBoundary } = useErrorBoundary();
  const createTracks = useGenomeViewerStore((state) => state.createTracks);
  const updateTrackContigId = useGenomeViewerStore((state) => state.updateTrackContigId);
  const show = useGenomeViewerStore((state) => state.showFeaturePopover);
  // const hasActiveAlignment = useGenomeViewerStore((state) => !!state.alignedFamily);
  const memberItem:
    | (Omit<MemberItem, "familyId"> & { familyId: number | undefined } & Partial<MainThreadHoverMessage>)
    | undefined = useGenomeViewerStore(
    useShallow((state) => {
      if (state.contextualMenuData?.id && state.contextualMenuData.trackId) {
        const track = state.tracks.get(state.contextualMenuData.trackId);

        if (track) {
          const group = state.trackGroups.get(track?.groupId);

          if (group) {
            return {
              ...state.contextualMenuData,
              featureId: state.contextualMenuData.id, // TypeScript is dumb, we need to reset it so that he's happy.
              trackId: state.contextualMenuData.trackId, // TypeScript is dumb, we need to reset it so that he's happy.
              familyId: state.contextualMenuData.orthologId,
              contigId: group.contigId,
              trackGroupId: group.groupId,
              start: state.contextualMenuData.start,
            };
          }
        }
      }

      return undefined;
    }),
  );
  const alignedFamily = useGenomeViewerStore((state) => state.alignedFamily);
  const syntenyId = useGenomeViewerStore((state) => state.syntenyOrganism.id);
  const syntenyWorker = useGenomeViewerStore((state) => state.syntenyWorker);
  const activeSyntenyType = useGenomeViewerStore((state) => state.activeSyntenyType);
  const [group, groupId, track] = useGenomeViewerStore(
    useShallow((state) => {
      const none = [undefined, undefined, undefined];

      if (!memberItem) return none;

      const track = state.tracks.get(memberItem.trackId);
      const group = track ? state.trackGroups.get(track.groupId) : undefined;

      if (!track || !group) return none;

      return [state.trackGroups.get(track.groupId), track.groupId, track];
    }),
  );

  const currentTrackType = useGenomeViewerStore(
    useShallow((state) => {
      const trackId = state.contextualMenuData?.trackId;
      if (!trackId) return undefined;
      const track = state.tracks.get(trackId);
      return track?.trackType;
    }),
  );

  const [paralogs, setParalogs] = useState<GenomeMember[]>([]);
  const [sameQty, setSameQty] = useState<number>();
  const [hasOrthologs, setHasOrthologs] = useState(true); // In relationship with isLoading
  const [isLoading, setIsLoading] = useState(true); // We'll load
  const isLongLoading = useLongLoading(isLoading, 300);
  const [info, setInfo] = useState<FeatureInfo | undefined>(undefined);

  useEffect(() => {
    // reset
    setParalogs([]);
    setSameQty(undefined);
    setInfo(undefined);
    setHasOrthologs(true); // Align by default
    setIsLoading(true);

    if (show) {
      async function load() {
        if (currentTrackType && memberItem?.featureId) {
          const featureInfo = await fetchFeatureInfo(
            getGenomicFeatureTypeFromTrackType(currentTrackType),
            memberItem.featureId,
          );
          setInfo(featureInfo);
        }
        if (memberItem?.trackId && memberItem.familyId && group?.organismId && activeSyntenyType) {
          const homologs = await getFeaturesFamilyMembersByGenomeOrThrow(
            memberItem.familyId,
            syntenyId!,
            activeSyntenyType,
            memberItem.featureId,
          );
          const paralogsData = homologs?.get(group.organismId) ?? [];
          // const membersQty = [...homologs.values()].reduce((prev, cur) => prev + cur.length, 0);

          // Sorting paralogsData first by contig name and then by start position
          const sortedParalogsData = (homologs?.get(group.organismId) ?? []).sort((a, b) => {
            const contigComparison = sortAlpha(a.contigName, b.contigName);
            if (contigComparison !== 0) return contigComparison;
            return a.start - b.start;
          });

          setParalogs(sortedParalogsData);
          setSameQty(homologs.size);

          const orthologs = Array.from(homologs.values())
            .flatMap((members) => members.filter((m) => ![...paralogsData.values()].includes(m)))
            .filter((o) => o.organismId !== group.organismId);

          return orthologs.length > 0;
        }

        return false;
      }

      load()
        .then((hasOrthologs) => {
          setIsLoading(false);
          setHasOrthologs(hasOrthologs);
        })
        .catch((error: Error | FetchError) => {
          if (error instanceof FetchError && error.statusCode === 401) {
            showBoundary(error);
          }

          notify.error({ title: "Could not load feature info", children: error.message });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    memberItem?.orthologId,
    memberItem?.featureId,
    group?.organismId,
    alignedFamily?.familyId,
    activeSyntenyType,
    currentTrackType,
    show,
  ]);

  const close = useCallback(() => {
    const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;
    Object.assign(contextualMenu.style, {
      display: "none",
    });
    useGenomeViewerStore.setState({ showFeaturePopover: false, contextualMenuData: undefined });
  }, []);

  useEffect(() => {
    document.body.addEventListener("pointerdown", handleFeaturePopover, true);

    return () => {
      document.body.removeEventListener("pointerdown", handleFeaturePopover, true);
    };
  }, []);

  const handleAlign = useCallback(() => {
    // familyId is not always provided, but is mandatory to execute an alignment
    if (memberItem?.familyId != undefined) {
      align(memberItem as MemberItem);
    }
    close();
  }, [close, memberItem]);

  const handleAlignOther = useCallback(
    (id: number, contigId: number, start: number, familyId: number) => {
      if (group && track && memberItem) {
        updateTrackContigId(track.groupId, contigId, (memberItem.start ?? 0) + group.graphics.baseShift - start);
        refreshTracks();
        refreshMain();

        // we're aligned with a member of the same family of the currently highlighted family
        if (alignedFamily?.familyId === familyId) {
          // TODO I am wondering if we shouldn't also add the new member to the alignedFamily instead? So no need to call align?
          // Somehow align is also kind of broken
          align({
            featureId: id,
            trackId: memberItem.trackId,
            familyId,
            trackGroupId: track.groupId,
            contigId,
            start,
          });
        } else {
          // Delete the alignedFamily Member because we moved the track so somewhere else, unrelated to the current alignment.
          // If we keep the members in the family, we will be desynchronized from what on screen and if you align from a member of the still aligned family on screen,
          // it won't be able to recall this track to the correct members because for the app, the members is still in the aligned family, which is visually not true anymore.
          if (alignedFamily) {
            alignedFamily.members = alignedFamily?.members.filter((m) => m.trackId !== memberItem.trackId);
            useGenomeViewerStore.setState({ alignedFamily });
            // refreshTracks(); // Redraw alignment without the deleted member
            sendSyntenyRenderMessage(syntenyWorker);
          }
        }
      }
      close();
    },
    [alignedFamily, close, group, memberItem, track, updateTrackContigId, syntenyWorker],
  );

  const handleNewTrack = useCallback(
    (id: number, contigId: number, start: number, familyId: number) => {
      if (group && groupId && track && memberItem) {
        const newGroup = {
          contigId: contigId,
          genomeId: group.organismId,
          // This is where we pre-shift the track
          toBase: (memberItem.start ?? 0) + group.graphics.baseShift - start,
        };

        const newTrack: TrackConfig =
          track.trackType === TrackEnum.NUCLEOTIDE_FEATURE
            ? {
                trackType: track.trackType,
                group: newGroup,
                options: {
                  subType: track.subType!,
                  color: track.color,
                  text: TrackTextEnum.INNER,
                  isVisible: true,
                },
              }
            : {
                trackType: track.trackType,
                group: newGroup,
              };

        const newTrackIdSet = createTracks([newTrack], showBoundary, group.groupId);

        // Manually updating the aligned family
        const to = newTrackIdSet.values().next().value;

        if (to && alignedFamily?.familyId === familyId) {
          alignedFamily.members.push({ featureId: id, trackId: to.trackId });
          useGenomeViewerStore.setState({ alignedFamily });
        }

        //! As we are pre-shifting tracks and manually updating the aligned family, there is no need to align here.
        refreshTracks();
      }
    },
    [alignedFamily, createTracks, group, groupId, memberItem, showBoundary, track],
  );

  const showSkeleton = isLoading || isLongLoading || !info;

  return (
    <div
      id="genome-viewer-contextual-menu"
      data-testid="genome-viewer-contextual-menu"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        display: "none",
        zIndex: 10,
      }}
    >
      <DialogComponent
        key={`feature-${memberItem?.featureId}`}
        title={memberItem?.name}
        subtitle={
          currentTrackType === TrackEnum.NUCLEOTIDE_FEATURE
            ? "DNA Feature"
            : syntenyViewerDefaultTracks.find((trackType) => trackType.type === currentTrackType)?.name
        }
        titleTruncateMiddle
        asPanelHeader
        contentStyle={{ height: "fit-content", minWidth: 350, maxWidth: 700 }}
        style={{ maxWidth: 700 }}
        actions={
          showSkeleton
            ? undefined
            : [
                paralogs.length > 0 ? (
                  <Tag
                    key="paralog-count"
                    intentColor={colors.secondaryMain}
                    tooltip={{ title: "Syntelogs in This Genome" }}
                  >
                    {/* + itself */}
                    {paralogs.length + 1}
                  </Tag>
                ) : null,
                memberItem ? (
                  <TriggerButtonGroup key={"open-group"}>
                    <OpenSummaryPageButton
                      key={"open-summary-page"}
                      id={memberItem.featureId}
                      proteinId={info.protein?.id}
                      trackId={memberItem.trackId}
                    />
                    <OpenGenomeViewerButton
                      key={"open-in-genome-viewer"}
                      id={memberItem.featureId}
                      proteinId={info.protein?.id}
                      trackId={memberItem.trackId}
                    />
                  </TriggerButtonGroup>
                ) : null,
              ]
        }
      >
        <Card noBorder noPadding>
          {showSkeleton ? (
            <CardSection>
              <Skeleton />
            </CardSection>
          ) : (
            <>
              <CardSection>
                {currentTrackType === TrackEnum.NUCLEOTIDE_FEATURE ? (
                  <TrackNuFeatureTooltip
                    info={info}
                    currentTrackType={currentTrackType}
                    contigName={group?.contigName}
                  />
                ) : (
                  <TrackFeatureTooltip info={info} currentTrackType={currentTrackType} contigName={group?.contigName} />
                )}
              </CardSection>
              {!!activeSyntenyType && getTrackTypeBySyntenyType(activeSyntenyType) === currentTrackType && (
                <>
                  <CardRowSimple label={`Syntelogs in ${sameQty ?? 0} ${pluralize("Genome", sameQty)}`}>
                    <ActionButton
                      isDisabled={!hasOrthologs}
                      onPress={handleAlign}
                      isLoading={isLoading}
                      label={hasOrthologs || isLongLoading ? "Align Genomes" : "No Syntelogs in Other Genomes"}
                      style={{ marginTop: 5 }}
                    />
                  </CardRowSimple>
                  {paralogs.length > 0 && (
                    <CardRowSimple
                      label={
                        activeSyntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT
                          ? "Other Syntenic Regions"
                          : "Other Syntelogs in This Genome"
                      }
                    >
                      <VStack>
                        {paralogs.map((member) => (
                          <ItemButton
                            key={String(member.locationId)}
                            member={member}
                            onPress={() =>
                              handleAlignOther(member.locationId, member.contigId, member.start, member.familyId)
                            }
                            hoverElement={
                              <HStack spacing={1}>
                                <TriggerButton
                                  icon="start"
                                  label="Go"
                                  onPress={() =>
                                    handleAlignOther(member.locationId, member.contigId, member.start, member.familyId)
                                  }
                                />
                                <TriggerButton
                                  icon="variable_add"
                                  label="Add Track"
                                  onPress={() =>
                                    handleNewTrack(member.locationId, member.contigId, member.start, member.familyId)
                                  }
                                />
                              </HStack>
                            }
                          />
                        ))}
                      </VStack>
                    </CardRowSimple>
                  )}
                </>
              )}
            </>
          )}
        </Card>
      </DialogComponent>
    </div>
  );
}

const ItemButton = ({
  member,
  onPress,
  hoverElement,
}: {
  member: GenomeMember;
  onPress: (e: PressEvent) => void;
  hoverElement: ReactNode;
}) => {
  const { pressProps } = usePress({ onPress });
  const { hoverProps, isHovered } = useHover({});

  const variants = {
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
      },
    },
    hidden: {
      opacity: 0,
      transition: {
        duration: 0,
      },
    },
  };

  return (
    <HStack
      domProps={hoverProps}
      style={{ background: isHovered ? "var(--hover-background)" : undefined, paddingRight: 5, borderRadius: 3 }}
    >
      <div {...pressProps} style={{ width: "100%" }}>
        <MenuOptionItem
          title={member.memberName}
          description={formatRegion(member.contigName, member.start, member.stop)}
          style={{ maxWidth: 600 }} // The default value in the widget is set to 280px
        />
      </div>
      <motion.div
        variants={variants}
        initial="hidden"
        animate={isHovered ? "visible" : "hidden"}
        style={{ position: "relative", display: "flex", alignItems: "center" }}
      >
        {hoverElement}
      </motion.div>
    </HStack>
  );
};
